// ── NAVIGATION GUARD ──
// Lets a page register a "before-navigate" guard so that things like the
// SiteHeader hamburger menu can give the active page a chance to ask the
// captain to confirm before leaving (e.g., "save your unsaved Dictionary
// selections?"). The guard receives a `proceed` callback that the page
// invokes once the captain has answered the prompt.
//
// Browser-level exits (closing the tab, refreshing, navigating to an
// external URL) can't be intercepted with a custom React modal — those still
// fall back to the native `beforeunload` dialog at the page level.

import { createContext, useCallback, useContext, useRef } from 'react';

const NavigationGuardContext = createContext(null);

export function NavigationGuardProvider({ children }) {
  // We use a ref so updating the guard doesn't re-render every consumer of
  // the context. The guard is read at navigation time, not at render time.
  const guardRef = useRef(null);

  // Page calls this to install or remove its guard.
  // Pass `null` to unregister.
  const setGuard = useCallback((fn) => {
    guardRef.current = fn || null;
  }, []);

  // SiteHeader (or any other navigator) calls this to ask the active page
  // whether it's OK to navigate. If no guard is registered, proceed
  // immediately. Otherwise hand off to the guard, which is responsible for
  // calling `proceed()` (allow) or doing nothing (block).
  const checkGuard = useCallback((proceed, intent) => {
    const fn = guardRef.current;
    if (typeof fn === 'function') {
      fn(proceed, intent || {});
    } else {
      proceed();
    }
  }, []);

  return (
    <NavigationGuardContext.Provider value={{ setGuard, checkGuard }}>
      {children}
    </NavigationGuardContext.Provider>
  );
}

// Returns the navigation guard API. Falls back to a no-op object if no
// provider is mounted, so consumers don't crash in unwrapped contexts.
export function useNavigationGuard() {
  return useContext(NavigationGuardContext) || {
    setGuard: () => {},
    checkGuard: (proceed) => proceed(),
  };
}
